<div class="ca-page">
	<div class="ca-header">
		<div class="ca-header-left">
			<h2 class="ca-title">
				<mat-icon class="ca-title-icon">credit_card</mat-icon>
				Uncaptured Authorizations
			</h2>
			<p class="ca-subtitle">Invoices were already created — capture the pre-auth against each invoice amount.</p>
		</div>
		<div class="ca-header-actions">
			<button mat-stroked-button (click)="load()" [disabled]="loading">
				<mat-spinner *ngIf="loading" diameter="18"></mat-spinner>
				<mat-icon *ngIf="!loading">refresh</mat-icon>
				Refresh
			</button>
			<button mat-raised-button color="accent" (click)="captureAll()" [disabled]="loading || pendingCount === 0"
				matTooltip="Capture all items that have a matched invoice">
				<mat-icon>done_all</mat-icon>
				Capture All Invoiced ({{ pendingCount }})
			</button>
		</div>
	</div>

	<div class="ca-not-enabled" *ngIf="!preauthEnabled && !loading">
		<mat-icon>info_outline</mat-icon>
		<span>PreAuth is not enabled in system configuration.</span>
	</div>

	<div class="ca-stat-row" *ngIf="preauthEnabled && !loading">
		<div class="ca-stat" [ngClass]="pendingCount > 0 ? 'ca-stat-warn' : 'ca-stat-ok'">
			<span class="ca-stat-val">{{ pendingCount }}</span>
			<span class="ca-stat-label">Pending capture</span>
		</div>
		<div class="ca-stat ca-stat-alert" *ngIf="mismatchCount > 0">
			<span class="ca-stat-val">{{ mismatchCount }}</span>
			<span class="ca-stat-label">Amount mismatch</span>
		</div>
		<div class="ca-stat ca-stat-alert" *ngIf="expiringCount > 0">
			<span class="ca-stat-val">{{ expiringCount }}</span>
			<span class="ca-stat-label">Expiring soon</span>
		</div>
		<div class="ca-stat ca-stat-info" *ngIf="noInvoiceCount > 0">
			<span class="ca-stat-val">{{ noInvoiceCount }}</span>
			<span class="ca-stat-label">No invoice yet</span>
		</div>
		<div class="ca-stat-toggle">
			<mat-slide-toggle [(ngModel)]="showCaptured">Show captured</mat-slide-toggle>
		</div>
	</div>

	<div class="ca-loading-wrap" *ngIf="loading">
		<mat-spinner diameter="48"></mat-spinner>
		<p>Loading authorizations…</p>
	</div>

	<div class="ca-empty" *ngIf="!loading && preauthEnabled && visibleItems.length === 0">
		<mat-icon>check_circle_outline</mat-icon>
		<span>No uncaptured authorizations found.</span>
	</div>

	<div class="ca-table-wrap" *ngIf="!loading && preauthEnabled && visibleItems.length > 0">
		<table class="ca-table">
			<thead>
				<tr>
					<th>Order</th>
					<th>Customer</th>
					<th>Auth Date</th>
					<th>Expires</th>
					<th class="text-right">Auth Amount</th>
					<th>Invoice #</th>
					<th class="text-right">Invoice Total</th>
					<th class="text-right ca-capture-col">Capture Amount</th>
					<th>Status</th>
					<th></th>
				</tr>
			</thead>
			<tbody>
				<tr *ngFor="let item of visibleItems" [ngClass]="statusClass(item)">
					<td class="ca-orderno">
						<strong>{{ item.orderno }}</strong>
						<div class="ca-debtorno">{{ item.debtorno }}</div>
					</td>
					<td>{{ item.customer_name || '—' }}</td>
					<td>{{ item.transdate | date:'shortDate' }}</td>
					<td>
						<span [ngClass]="item.expiring_soon ? 'ca-expiring' : ''">
							{{ item.days_until_expiry >= 0 ? item.days_until_expiry + 'd' : 'Expired' }}
						</span>
					</td>
					<td class="text-right ca-amount">{{ fmt(item.auth_amount) }}</td>
					<td>
						<span *ngIf="item.invoice_transno" class="ca-invoice-no">{{ item.invoice_transno }}</span>
						<span *ngIf="!item.invoice_transno" class="ca-no-invoice" matTooltip="Invoice not yet created — cannot capture">No invoice</span>
					</td>
					<td class="text-right ca-amount">
						<span *ngIf="item.invoice_total !== null">
							{{ fmt(item.invoice_total) }}
							<mat-icon class="ca-mismatch-icon" *ngIf="item.mismatch"
								matTooltip="Auth amount ({{ fmt(item.auth_amount) }}) differs from invoice total ({{ fmt(item.invoice_total) }})">
								warning
							</mat-icon>
						</span>
						<span *ngIf="item.invoice_total === null" class="text-muted">—</span>
					</td>
					<td class="ca-capture-col">
						<ng-container *ngIf="!item.captured">
							<mat-form-field appearance="outline" class="ca-amount-field">
								<input matInput type="number" min="0.01" step="0.01"
									[(ngModel)]="item.capture_amount"
									[disabled]="!!item.capturing"
									placeholder="Amount">
							</mat-form-field>
						</ng-container>
						<span *ngIf="item.captured" class="ca-captured-amount">{{ fmt(item.capture_amount) }}</span>
					</td>
					<td>
						<span class="ca-status-badge" *ngIf="!item.captured && !item.capture_error">
							{{ authStatusLabel(item.auth_status) }}
						</span>
						<span class="ca-status-badge ca-status-captured" *ngIf="item.captured">
							<mat-icon>check_circle</mat-icon> Captured
						</span>
						<span class="ca-status-badge ca-status-error" *ngIf="item.capture_error && !item.captured"
							[matTooltip]="item.capture_error">
							<mat-icon>error</mat-icon> Failed
						</span>
					</td>
					<td class="ca-action-col">
						<button mat-raised-button color="primary"
							*ngIf="!item.captured"
							[disabled]="!!item.capturing || !item.capture_amount || item.capture_amount <= 0"
							(click)="capture(item)"
							matTooltip="Capture this authorization">
							<mat-spinner *ngIf="item.capturing" diameter="18"></mat-spinner>
							<mat-icon *ngIf="!item.capturing">credit_card</mat-icon>
							{{ item.capturing ? 'Capturing…' : 'Capture' }}
						</button>
						<span *ngIf="item.captured" class="ca-done-label">
							<mat-icon>done</mat-icon> Done
						</span>
					</td>
				</tr>
			</tbody>
		</table>
	</div>

	<div class="ca-legend" *ngIf="!loading && preauthEnabled && visibleItems.length > 0">
		<span class="ca-legend-item ca-row-mismatch">Amount mismatch</span>
		<span class="ca-legend-item ca-row-expiring">Expiring soon</span>
		<span class="ca-legend-item ca-row-noinvoice">No invoice</span>
		<span class="ca-legend-item ca-row-captured">Captured</span>
	</div>
</div>
